import type { ReactNode } from "react";
import {
	formatVisitNumbers,
	groupByAddress,
	type VisitAddressSnapshot,
} from "@/components/admin-portal/appointment/update-address-scope";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

type OrderVisit = {
	visitNumber: number;
	address: VisitAddressSnapshot | null;
};

export type OrderAddressChange = {
	changedAt: string;
	changedByName: string | null;
	source: "active_address" | "manual" | null;
	new: VisitAddressSnapshot;
	visits: { visitNumber: number; old: VisitAddressSnapshot }[];
};

// How the new address was set, read after the admin's name: "by Dendy · via manual entry".
const SOURCE_LABELS = {
	active_address: "via patient's active address",
	manual: "via manual entry",
} as const;

const FIELDS: {
	label: string;
	read: (address: VisitAddressSnapshot) => string | null;
}[] = [
	{ label: "Address", read: (a) => a.addressLine || null },
	{ label: "City", read: (a) => a.city || null },
	{ label: "Postal code", read: (a) => a.postalCode || null },
	{
		label: "Coordinates",
		read: (a) =>
			a.latitude == null || a.longitude == null
				? null
				: `${a.latitude}, ${a.longitude}`,
	},
	{ label: "Notes", read: (a) => a.notes || null },
];

const VISIT_LABEL_CLASS =
	"text-[11px] uppercase tracking-wide text-muted-foreground";

// One stop on the rail: a square node, and the line down to the next stop. The last stop
// has no line below it, and a dashed node.
function TimelineStop({
	current = false,
	last = false,
	children,
}: {
	current?: boolean;
	last?: boolean;
	children: ReactNode;
}) {
	return (
		<li className="relative pb-4 pl-6 last:pb-0">
			{!last && (
				<span
					aria-hidden
					className="absolute top-3 bottom-0 left-[4.5px] w-px bg-border"
				/>
			)}
			<span
				aria-hidden
				className={cn(
					"absolute top-1 left-0 size-2.5 border",
					current
						? "border-primary bg-primary"
						: "border-muted-foreground/40 bg-background",
					last && "border-dashed",
				)}
			/>
			{children}
		</li>
	);
}

// Order Details' Address timeline: where the visits are served now, at the head of the rail,
// then every Update Address submit on the order, newest first.
export function OrderAddressSection({
	appointments,
	changes,
	formatChangedAt,
}: {
	appointments: OrderVisit[];
	changes: OrderAddressChange[];
	formatChangedAt: (value: string) => string;
}) {
	const groups = groupByAddress(appointments, (visit) => visit.address);

	return (
		<div>
			<h4 className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold mb-3">
				Address timeline
			</h4>

			<ol>
				<TimelineStop current>
					<p className="mb-1.5 text-xs font-semibold text-primary">Now</p>
					<div className="grid gap-2.5">
						{groups.map(({ address, items }) => (
							<div key={items[0].visitNumber} className="min-w-0 space-y-0.5">
								<p className={VISIT_LABEL_CLASS}>
									Visit{" "}
									{formatVisitNumbers(items.map((visit) => visit.visitNumber))}
									{address?.city && ` · ${address.city}`}
								</p>
								{address ? (
									<>
										<p className="text-sm font-semibold leading-snug text-pretty break-words">
											{[address.addressLine, address.postalCode]
												.filter(Boolean)
												.join(", ")}
										</p>
										{address.notes && (
											<p className="text-xs text-muted-foreground text-pretty break-words">
												{address.notes}
											</p>
										)}
									</>
								) : (
									<p className="text-sm text-muted-foreground">
										No address yet
									</p>
								)}
							</div>
						))}
					</div>
				</TimelineStop>

				{changes.map((change, index) => (
					<TimelineStop
						key={`${change.changedAt}-${change.visits[0]?.visitNumber}-${index}`}
					>
						<div className="mb-1.5 flex flex-wrap items-center gap-x-2 gap-y-1">
							<p className="text-xs text-muted-foreground">
								<span className="font-medium text-foreground tabular-nums">
									{formatChangedAt(change.changedAt)}
								</span>{" "}
								· by {change.changedByName || "—"}
							</p>
							{change.source && (
								<Badge
									variant="outline"
									className="rounded-full border p-0 px-1.5 text-[10px] font-bold"
								>
									{SOURCE_LABELS[change.source]}
								</Badge>
							)}
						</div>

						{/* Visits that moved from the same old address share one block. */}
						<div className="grid gap-2.5">
							{groupByAddress(change.visits, (visit) => visit.old).map(
								({ address: old, items }) => (
									<div key={items[0].visitNumber} className="min-w-0 space-y-1">
										<p className={VISIT_LABEL_CLASS}>
											Visit{" "}
											{formatVisitNumbers(
												items.map((visit) => visit.visitNumber),
											)}
										</p>
										<dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-0.5 text-sm">
											{FIELDS.map(({ label, read }) => {
												const before = old ? read(old) : null;
												const after = read(change.new);
												if (before === after) return null;

												return (
													<div key={label} className="contents">
														<dt className="pt-0.5 text-xs text-muted-foreground">
															{label}
														</dt>
														<dd className="text-pretty break-words">
															<span className="text-muted-foreground">
																{before ?? "—"}
															</span>
															<span className="mx-1 text-primary">→</span>
															<span className="font-semibold">
																{after ?? "—"}
															</span>
														</dd>
													</div>
												);
											})}
										</dl>
									</div>
								),
							)}
						</div>
					</TimelineStop>
				))}

				{/* The rail ends where recording began. */}
				<TimelineStop last>
					<p className="text-xs text-muted-foreground">
						{changes.length === 0 ? "No address changes recorded yet. " : ""}
						Changes made before this release are not recorded here.
					</p>
				</TimelineStop>
			</ol>
		</div>
	);
}
